import { Typography } from '@mui/material';

/**
 * Propósito: Normaliza og_co/og_comp + og_job/og_job_no de cualquier shape
 * (jobs list, ticket, invoice, dispatch, earnings) a una referencia visible.
 */
export function resolveOriginalJobRef(job) {
  if (!job || typeof job !== 'object') {
    /**
     * Output:
     * @returns {null} — Sin job
     */
    return null;
  }

  const companyId = job.og_co || job.og_comp || null;
  const jobNo = job.og_job ?? job.og_job_no ?? null;
  if (companyId == null && jobNo == null) {
    /**
     * Output:
     * @returns {null} — El job no es un clon / no trae OG
     */
    return null;
  }

  const reference = [companyId, jobNo].filter((part) => part != null && part !== '').join('-');
  if (!reference) {
    /**
     * Output:
     * @returns {null} — Campos OG vacíos
     */
    return null;
  }

  /**
   * Output:
   * @returns {{reference: string, companyName: string|null}}
   *   - reference: "421-17"
   *   - companyName: parent company (cliente) si vino del API
   */
  return {
    reference,
    companyName: job.og_comp_name || null,
  };
}

/**
 * Propósito: Match de búsqueda contra la referencia OG y el nombre parent.
 */
export function originalJobMatchesSearch(job, token) {
  const originalRef = resolveOriginalJobRef(job);
  if (!originalRef || token == null || token === '') {
    /**
     * Output:
     * @returns {boolean} false — Sin OG o sin token
     */
    return false;
  }

  const needle = String(token).toLowerCase();
  /**
   * Output:
   * @returns {boolean} true — Token aparece en OG ref o parent name
   */
  return (
    originalRef.reference.toLowerCase().includes(needle) ||
    Boolean(originalRef.companyName && originalRef.companyName.toLowerCase().includes(needle))
  );
}

/**
 * Propósito: Label de autocomplete de dispatch: clone + OG para que el filtro
 * nativo también encuentre por el job original.
 */
export function formatDispatchJobOptionLabel(option) {
  if (!option) {
    /**
     * Output:
     * @returns {string} '' — Option vacía
     */
    return '';
  }

  const cloneRef = `${option.company_id}-${option.job_no}`;
  const originalRef = resolveOriginalJobRef(option);
  const ogPart = originalRef ? `; OG: ${originalRef.reference}` : '';
  const source = option.origin ? option.origin : option.quarry_pit;

  /**
   * Output:
   * @returns {string} Label plano para getOptionLabel / filter
   */
  return `${cloneRef}${ogPart}; ${option.customer_name}; ${source}; ${option.destination}; ${option.material}; ${option.unit_name}-${option.unit_metric}`;
}

const VARIANT_STYLES = {
  stacked: {
    display: 'block',
    fontSize: 10,
    lineHeight: 1.15,
    fontWeight: 400,
    color: '#9c9c9c',
    marginTop: 1,
  },
  compact: {
    display: 'block',
    fontSize: 9,
    lineHeight: 1.1,
    fontWeight: 400,
    color: '#9c9c9c',
    marginTop: 0,
  },
  subtle: {
    display: 'block',
    fontSize: 10,
    lineHeight: 1.15,
    fontWeight: 400,
    color: '#a8a8a8',
    marginTop: 1,
  },
  supplemental: {
    display: 'block',
    fontSize: 10,
    lineHeight: 1.15,
    fontWeight: 400,
    color: '#9c9c9c',
    marginTop: 1,
  },
  inline: {
    display: 'inline',
    fontSize: '0.85em',
    lineHeight: 'inherit',
    fontWeight: 400,
    color: '#8a8a8a',
    marginLeft: 8,
  },
  onDark: {
    display: 'block',
    fontSize: 9,
    lineHeight: 1.1,
    fontWeight: 400,
    color: 'rgba(255,255,255,0.42)',
    marginTop: 0,
  },
  report: {
    display: 'block',
    fontSize: 11,
    lineHeight: 1.15,
    fontWeight: 400,
    color: '#9c9c9c',
    marginTop: 2,
  },
};

/**
 * Propósito: Whisper del job original debajo del clone: visible, no protagonista.
 */
export default function OriginalJobLabel({ job, variant = 'stacked', sx }) {
  const originalRef = resolveOriginalJobRef(job);
  if (!originalRef) {
    /**
     * Output:
     * @returns {null} — No renderiza si el job no tiene original
     */
    return null;
  }

  const style = { ...(VARIANT_STYLES[variant] || VARIANT_STYLES.stacked), ...sx };

  /**
   * Output:
   * @returns {JSX.Element} Línea suave "OG: 421-17" siempre en inglés
   */
  return (
    <Typography component='span' sx={style} title={`OG: ${originalRef.reference}`}>
      OG: {originalRef.reference}
    </Typography>
  );
}
